// Modal (drawing v2.6, screen 2 "Dialog", `confirmModal()`; reference section
// 7 and 8): up to 520 wide, radius 14, sh3, the page dimmed behind; the 44px
// icon and the title are centered on each other. role="dialog", aria-modal;
// the page behind is inert and does not scroll. A destructive dialog opens
// with focus on Cancel, never on the red button; any other opens on its
// first field. Tab stays inside; Esc, Cancel or a click outside close it, and
// focus returns to the button that opened it. On a phone it is a bottom sheet
// with full-width buttons, Cancel last. The confirm's button names the act.

import { useEffect, useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

let locks = 0;
let priorOverflow = "";
let priorPad = "";
function lock() {
  if (locks++ === 0) {
    const root = document.documentElement;
    priorOverflow = root.style.overflow;
    priorPad = root.style.paddingRight;
    const bar = window.innerWidth - root.clientWidth;
    root.style.overflow = "hidden";
    if (bar > 0) root.style.paddingRight = `${bar}px`;
  }
}
function unlock() {
  if (--locks <= 0) {
    locks = 0;
    const root = document.documentElement;
    root.style.overflow = priorOverflow;
    root.style.paddingRight = priorPad;
  }
}

export function useDialogBehavior(open: boolean, onClose: () => void, ref: React.RefObject<HTMLElement | null>, initialFocus?: "cancel" | "first") {
  useLayoutEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el) return;
    const previously = document.activeElement as HTMLElement | null;
    const targets = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
    const cancel = el.querySelector<HTMLElement>("[data-initial]");
    (initialFocus === "cancel" && cancel ? cancel : targets[0] ?? el).focus({ preventScroll: true });
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = Array.from(el!.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !el!.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    lock();
    return () => {
      document.removeEventListener("keydown", onKey);
      unlock();
      previously?.focus?.({ preventScroll: true });
    };
  }, [open, onClose, ref, initialFocus]);
}

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** The 44px mark beside the title (an icon in a tile). */
  icon?: ReactNode;
  /** Bullets and fields: what changes, what stays, how to undo. */
  children: ReactNode;
  /** Cancel first, then the one button that names the act. */
  footer: ReactNode;
  /** A destructive dialog opens with focus on Cancel (mark it with data-initial). */
  destructive?: boolean;
  className?: string;
}

export function Modal({ open, onClose, title, icon, children, footer, destructive, className }: ModalProps) {
  const shell = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  useDialogBehavior(open, onClose, shell, destructive ? "cancel" : "first");
  useEffect(() => {
    // A mounted dialog never drops its listeners when the page re-renders.
  }, []);
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      className="pg-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={shell} className={cx("pg-modal", className)} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="pg-modal__h">
          {icon && <span className={cx("pg-mk", destructive && "pg-mk--w")}>{icon}</span>}
          <h2 className="pg-modal__title" id={titleId}>
            {title}
          </h2>
        </header>
        <div className="pg-modal__b">{children}</div>
        <footer className="pg-modal__f">{footer}</footer>
      </div>
    </div>,
    document.body,
  );
}
